import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * useTrafficSource
 * ----------------
 * Detects the incoming visitor's traffic source from URL query params on first
 * page load and PERSISTS it in sessionStorage for the entire session. Internal
 * navigation cannot overwrite a previously-persisted source — only a NEW
 * URL-provided source or a fresh browser tab (sessionStorage clears itself).
 *
 * Supported explicit sources (via ?source=<name>):
 *   prolific · reddit · facebook · linkedin · surveycircle · surveyswap
 *
 * Prolific-specific auto-detection:
 *   Presence of ?PROLIFIC_PID=... (case-insensitive) also flags the session
 *   as Prolific and captures PROLIFIC_PID / STUDY_ID / SESSION_ID params.
 *
 * Fallbacks:
 *   'direct'  → no source param and no persisted value
 *   'unknown' → a source param was present but not in the whitelist
 */

const STORAGE_KEYS = {
  source: 'traffic_source',
  prolificPid: 'prolific_pid',
  studyId: 'study_id',
  prolificSessionId: 'prolific_session_id',
};

const KNOWN_SOURCES = new Set([
  'prolific',
  'reddit',
  'facebook',
  'linkedin',
  'surveycircle',
  'surveyswap',
]);

// Case-insensitive param lookup — Prolific uses UPPER_SNAKE_CASE.
const getParam = (params, name) => {
  const lower = name.toLowerCase();
  for (const [k, v] of params.entries()) {
    if (k.toLowerCase() === lower) return v;
  }
  return null;
};

const detectFromParams = (params) => {
  const rawSource = (getParam(params, 'source') || '').trim().toLowerCase();
  const prolificPid = getParam(params, 'PROLIFIC_PID');
  const studyId = getParam(params, 'STUDY_ID');
  const prolificSessionId = getParam(params, 'SESSION_ID');

  // Prolific wins if either the explicit flag or the auto-param is present.
  if (rawSource === 'prolific' || prolificPid) {
    return {
      source: 'prolific',
      prolificPid: prolificPid || null,
      studyId: studyId || null,
      prolificSessionId: prolificSessionId || null,
    };
  }

  if (KNOWN_SOURCES.has(rawSource)) {
    return { source: rawSource, prolificPid: null, studyId: null, prolificSessionId: null };
  }

  if (rawSource) {
    return { source: 'unknown', prolificPid: null, studyId: null, prolificSessionId: null };
  }

  return null; // no URL-driven detection this pageload
};

export const useTrafficSource = () => {
  const location = useLocation();

  // One-time detection & persistence (runs on every URL change but only writes
  // when the incoming URL contains new source info).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const detected = detectFromParams(params);
    if (!detected) return;

    // Only write if a source has NOT been persisted yet, OR the URL explicitly
    // provides a new one. This keeps internal navigation from clobbering source.
    const existing = sessionStorage.getItem(STORAGE_KEYS.source);
    if (!existing || existing !== detected.source) {
      sessionStorage.setItem(STORAGE_KEYS.source, detected.source);
      // v6.8 Sprint 4C iter 29 fix — when the detected source CHANGES (e.g. a
      // Prolific session followed by a SurveyCircle session in the same tab),
      // clear any previously-persisted Prolific IDs so they cannot leak into
      // the new source's research records.
      if (existing && existing !== detected.source) {
        sessionStorage.removeItem(STORAGE_KEYS.prolificPid);
        sessionStorage.removeItem(STORAGE_KEYS.studyId);
        sessionStorage.removeItem(STORAGE_KEYS.prolificSessionId);
      }
    }
    if (detected.prolificPid) sessionStorage.setItem(STORAGE_KEYS.prolificPid, detected.prolificPid);
    if (detected.studyId) sessionStorage.setItem(STORAGE_KEYS.studyId, detected.studyId);
    if (detected.prolificSessionId) sessionStorage.setItem(STORAGE_KEYS.prolificSessionId, detected.prolificSessionId);
  }, [location.search]);

  const value = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const detected = detectFromParams(params);

    const source =
      (detected && detected.source) ||
      sessionStorage.getItem(STORAGE_KEYS.source) ||
      'direct';

    const prolificPid =
      (detected && detected.prolificPid) ||
      sessionStorage.getItem(STORAGE_KEYS.prolificPid) ||
      null;

    const studyId =
      (detected && detected.studyId) ||
      sessionStorage.getItem(STORAGE_KEYS.studyId) ||
      null;

    const prolificSessionId =
      (detected && detected.prolificSessionId) ||
      sessionStorage.getItem(STORAGE_KEYS.prolificSessionId) ||
      null;

    return {
      source,
      prolificPid,
      studyId,
      prolificSessionId,
      isProlific: source === 'prolific',
      isSurveyCircle: source === 'surveycircle',
    };
  }, [location.search]);

  return value;
};

// Helper to clear traffic source (e.g. explicit "start over" reset).
export const clearTrafficSource = () => {
  sessionStorage.removeItem(STORAGE_KEYS.source);
  sessionStorage.removeItem(STORAGE_KEYS.prolificPid);
  sessionStorage.removeItem(STORAGE_KEYS.studyId);
  sessionStorage.removeItem(STORAGE_KEYS.prolificSessionId);
};
